import role24MfrsSchema from '../../../../assets/forms/role-750000-mfrs.form.json';
import role24MpersSchema from '../../../../assets/forms/role-750000-mpers.form.json';
import { pickSchema, MPERS_ACCOUNTING_STANDARD } from './schema-picker.util';
import { isEmptyCell } from '../validation-field-key.util';

/**
 * Shared business rules for Role 24 (Notes - Related Party Transactions).
 *
 * Role 24 is the final Tier 4 lift of Phase 2 and brings three concerns
 * together that none of the earlier sheets carried simultaneously:
 *
 *   - **Accounting-standard variant selection** (like roles 20 / 23): the
 *     same logical sheet ships as two sibling form JSONs
 *     (role-750000-mfrs.form.json and role-750000-mpers.form.json). The
 *     component picks between them at mount time based on
 *     filingPrerequisites.BasisOfAccountingStandardsApplied... and the pure
 *     validator mirrors that selection via pickRole24SchemaFile /
 *     pickRole24Schema.
 *
 *     NOTE: there is ALSO a `role-750000.form.json` at the same path, but
 *     the component's `loadSchema()` only ever fetches the -mfrs / -mpers
 *     variants. The no-suffix file is unused by role 24 today (likely a
 *     historical artefact). Selection helpers below therefore never return
 *     it.
 *
 *   - **Cross-sheet requiredIf** (like role 20's requiredIfCrossSheet): a
 *     control's required-ness can depend on another sheet's field. Role 24
 *     declares its rules with the `requiredIf: { sheet, qname, equals }`
 *     shape — note the property name is `qname` on role 24, not `field`
 *     like the role 20 `requiredIfCrossSheet` variant. The difference
 *     reflects two independently evolved patterns in the schema JSONs; we
 *     keep both alive rather than renormalising the schemas here (out of
 *     scope for a lift).
 *
 *   - **Table-row repetition** (like role 23): each tableColumn schema row
 *     expands into 16 flat sheetData keys — 8 related-party members x 2
 *     consolidation dimensions (Consolidated / Separate), CurrentYear only
 *     (role 24, unlike role 23, never builds PreviousYear controls; see
 *     buildForm()'s members list). So "table-row repetition" is a virtual
 *     expansion at form-build time, NOT an array-of-rows shape — and the
 *     SheetValidator / SheetValidationContext interface does NOT need to
 *     change for role 24.
 *
 * Both the editor's role24-750000-notes-related-party-transactions.component.ts
 * AND role24NotesRelatedPartyTransactionsValidator import from this file so
 * the selection literals, the member list, and the row-expansion primitives
 * live in exactly one place. If the backend ever renames the accounting-
 * standard enum, or the related-party member axis changes, only this file
 * moves — the parity-check harness will catch any consumer that drifted.
 */

/**
 * Literal value of
 * filingPrerequisites['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements']
 * that selects the MPERS schema. Any other value (including undefined, '',
 * or 'Malaysian Financial Reporting Standards') falls back to MFRS, matching
 * the component's `let schemaFile = 'role-750000-mfrs.form.json'` default
 * assignment in loadSchema().
 */
export const ROLE24_MPERS_ACCOUNTING_STANDARD = MPERS_ACCOUNTING_STANDARD;

/** Filename of the MFRS (default) variant schema. */
export const ROLE24_MFRS_SCHEMA_FILE = 'role-750000-mfrs.form.json';

/** Filename of the MPERS variant schema. */
export const ROLE24_MPERS_SCHEMA_FILE = 'role-750000-mpers.form.json';

/**
 * Related-party members used as the horizontal axis of each table row. The
 * empty string encodes the "Total" column which uses the base qname without
 * a member suffix (see the component's getXbrlControlName()).
 *
 * This ordering mirrors buildForm()'s `members` local array exactly. Role 24
 * uses IFRS-full member suffixes regardless of MFRS / MPERS variant — the
 * backend stores the suffix verbatim on both the MFRS and MPERS
 * ColumnSumValidationConfig.componentColumnQnames lists (the *prefix*
 * differs between configs but the *suffix* does not).
 */
export const ROLE24_MEMBER_SUFFIXES: ReadonlyArray<string> = [
  '', // Total (no member suffix)
  'ParentMember',
  'JointControlOrSignificantInfluenceMember',
  'SubsidiariesMember',
  'AssociatesMember',
  'JointVenturesWhereEntityIsVenturerMember',
  'KeyManagementPersonnelOfEntityOrParentMember',
  'OtherRelatedPartiesMember',
];

export type Role24Dimension = 'Consolidated' | 'Separate';

/**
 * The Consolidated-dimension gate (Nature of Financial Statements) and its
 * prereq-first / then-filingInfo resolution now live in the shared
 * dimension-visibility foundation: the gate descriptor is registered in
 * `dimension-visibility-registry.ts` (keyed by
 * `SHEET_KEYS.notesRelatedPartyTransactions`) and resolved by
 * `resolveDimensionVisibility()` in `dimension-visibility.util.ts`. Both the
 * pure validator (`resolveActiveDimensions()`) and the mounted editor
 * (`CrossReferenceService.getVisibility()`) call that one resolver, so the
 * Consolidated/Separate gate cannot drift between the two paths.
 */

/**
 * Narrow shape of a role 24 schema control used by the pure validator. The
 * schemas carry richer metadata (ui, error, ContextPolicy, validators) but
 * the validator only needs qname, type, required, requiredIf, and
 * ui.tableColumn — keeping this type tight makes the validator resilient to
 * unrelated schema additions.
 */
export interface Role24SchemaControl {
  qname: string;
  type?: string;
  required?: boolean;
  validators?: Array<{ name?: string }>;
  /**
   * Cross-sheet conditional required rule. Mirrors the component's
   * SchemaControl.requiredIf shape (note `qname` — not `field` like role
   * 20's requiredIfCrossSheet variant).
   */
  requiredIf?: {
    sheet: string;
    qname: string;
    equals: string;
  };
  ui?: {
    control?: string;
    level?: number;
    tableColumn?: boolean;
    readonly?: boolean;
    calculated?: boolean;
  };
}

/**
 * Schema-declared "sub-total = Σ component columns" reconciliation, mirroring
 * the mounted `ColumnSumValidationService` config shape. Role 24 declares one:
 * Total related party = Σ 7 related-party member columns.
 */
export interface Role24ColumnSumValidation {
  /** Sub-total column id — the literal `"Total"` for role 24 (no member suffix). */
  subTotalColumnQname: string;
  /** Component column qnames (full `prefix:LocalNameMember`). */
  componentColumnQnames: string[];
  subTotalLabel: string;
  componentLabels: string[];
  /**
   * Optional allow-list of row qnames the check applies to (the 31 approved
   * `tableColumn` rows). Present & non-empty → only those rows; absent → every
   * `tableColumn` non-header row. Honoured identically by the mounted
   * `ColumnSumValidationService` and `checkColumnSumErrors()` below.
   */
  appliesToRowQnames?: string[];
}

export interface Role24Schema {
  role: string;
  sheetId: string;
  label: string;
  columnSumValidations?: Role24ColumnSumValidation[];
  controls: Role24SchemaControl[];
}

/**
 * Resolve the correct role 24 schema filename for the given accounting
 * standard. Mirrors the component's loadSchema() selection so both paths
 * derive from the same rule.
 */
export function pickRole24SchemaFile(
  accountingStandard: string | undefined | null
): string {
  if (accountingStandard === ROLE24_MPERS_ACCOUNTING_STANDARD) {
    return ROLE24_MPERS_SCHEMA_FILE;
  }
  return ROLE24_MFRS_SCHEMA_FILE;
}

/**
 * Resolve the actual role 24 schema object for the given accounting
 * standard. Used by the pure validator which cannot fetch() at validate-time
 * (the listing/dashboard's cache-miss path runs synchronously) so both
 * variants are imported eagerly and selected from memory.
 */
export function pickRole24Schema(
  accountingStandard: string | undefined | null
): Role24Schema {
  return pickSchema(
    accountingStandard,
    role24MfrsSchema as unknown as Role24Schema,
    role24MpersSchema as unknown as Role24Schema
  );
}

/**
 * Expand a single schema row's qname into the eight column keys that the
 * component's computeRowValidationStatus() inspects for a given dimension
 * (Consolidated or Separate). Eight keys = 7 related-party members + 1
 * Total (no suffix). Role 24 ships CurrentYear only — no PreviousYear
 * controls are ever built (contrast with role 23 which builds both but
 * only validates CurrentYear).
 *
 * The pure validator replays this expansion and probes the flat sheetData
 * map at each key.
 */
export function buildRole24RowColumnKeys(
  qname: string,
  dimension: Role24Dimension
): string[] {
  return ROLE24_MEMBER_SUFFIXES.map((suffix) => {
    const base = suffix ? `${qname}:${suffix}` : qname;
    return `${base}[${dimension}][CurrentYear]`;
  });
}

/**
 * Build the flat sheetData key for ONE column of a row, mirroring the editor's
 * getRole24ValidationControlName():
 *   - the literal `"Total"` sub-total column → `{qname}[{dim}][CurrentYear]`
 *   - a member column qname (`prefix:LocalNameMember`) → the local-name suffix
 *     is used: `{qname}:{LocalNameMember}[{dim}][CurrentYear]`
 * The member *suffix* (not the full prefixed qname) is what `buildRole24RowColumnKeys`
 * and the editor's flat controls use, so the prefix (`ifrs-full:`/`ifrs-smes:`/`ssmt-*`)
 * is stripped here.
 */
export function buildRole24ColumnKey(
  qname: string,
  columnQname: string,
  dimension: Role24Dimension
): string {
  if (columnQname === 'Total') {
    return `${qname}[${dimension}][CurrentYear]`;
  }
  const suffix = columnQname.includes(':')
    ? columnQname.slice(columnQname.indexOf(':') + 1)
    : columnQname;
  return `${qname}:${suffix}[${dimension}][CurrentYear]`;
}

/**
 * Check if a single form-shaped value counts as "empty" for the purposes of
 * required-field validation. Matches the component's
 *   value === null || value === undefined || value === ''
 * guard in computeRowValidationStatus() / computeRowHasErrorCategory().
 */
export function isRole24Empty(value: unknown): boolean {
  return isEmptyCell(value);
}

/**
 * Is this schema control a validating (non-header) field?
 *
 * Mirrors the component's buildForm() skip-list: SectionHeader rows never
 * become form controls and therefore cannot fail validation.
 */
export function isRole24ValidatingControl(
  control: Role24SchemaControl
): boolean {
  return control.type !== 'SectionHeader';
}

/**
 * LB-001 fix-guard: a control is "statically required" when schema declares
 * `required === true` OR when `validators` contains an unconditional
 * `{ name: 'required' }` entry. Mirrors the component's getValidators()
 * which pushes Validators.required iff isFieldRequired() is true — and
 * isFieldRequired() today only inspects `control.required`, so an
 * unconditional `validators: [{ name: 'required' }]` entry without a
 * matching `required: true` would be silently dropped.
 *
 * Role 24's current schemas do NOT exhibit LB-001 (every required field
 * declares both `required: true` AND `validators: [{ name: 'required' }]`
 * or neither), so this function returns the same answer as
 * `control.required === true` today. Guard is kept for future schema edits
 * — see LB-009/10/11/12/13/16/17/18 in the latent-bugs ledger.
 */
export function isRole24StaticallyRequired(
  control: Role24SchemaControl
): boolean {
  if (control.required === true) return true;
  return Array.isArray(control.validators)
    ? control.validators.some((validator) => validator?.name === 'required')
    : false;
}
